import { isFunction } from '@tanstack/react-table';

import {
  datelikeCompare,
  numberlikeCompare,
  stringlikeCompare,
  stringlikeCompareCaseSensitive,
} from './compare-functions.js';
import type {
  DataTableColumnSort,
  DataTableCustomSortingFn,
  DataTableSortingProps,
} from './sorting-types.js';
import { useControllableState } from '../../../../core/hooks/useControllableState.js';
import { useDataTableConfigElement } from '../../../hooks/useDataTableConfig.js';
import { isGroupColumnDef } from '../../hooks/useTable/datatable-typeguards.js';
import type {
  Column,
  ColumnDef,
  Header,
  Row,
  RowData,
  Table,
  TableFeature,
  TableOptions,
} from '../../hooks/useTable/types.js';
import { assignRowMethods } from '../../hooks/useTable/utils.js';
import type { DataTableRowData } from '../../public.api.js';

/**
 * Typeguard function that checks whether the given column definition has a 'sortAccessor' defined.
 */
function isColumnDefWithSortAccessor(
  columnDef: unknown,
): columnDef is ColumnDef<unknown> {
  return (
    columnDef !== undefined &&
    columnDef !== null &&
    typeof columnDef === 'object' &&
    'sortAccessor' in columnDef &&
    columnDef.sortAccessor !== undefined
  );
}

/**
 * Configuration hook for the DataTable sorting feature.
 * @internal
 */
export function useSorting<TData extends DataTableRowData>(
  props: DataTableSortingProps,
  options: TableOptions<TData>,
): void {
  const configSortable = useDataTableConfigElement('sortable');

  const [sorting, setSorting] = useControllableState<DataTableColumnSort[]>({
    value: props.sortBy,
    defaultValue: props.defaultSortBy,
    onChange: props.onSortByChange,
  });

  const { sortable: sortableProp } = props;
  let sortable = false;

  if (sortableProp !== undefined) {
    if (typeof sortableProp === 'boolean') {
      sortable = sortableProp;
      options.manualSorting = false;
    } else {
      sortable = true;
      // To do: should reconsider is there a better and simpler way (simpler API exposed) that we can check wheater the consumer wants to keep default sorting
      // or they want to implement custom data pre sorting
      if (sortableProp.manualSort === true) {
        options.manualSorting = true;
      } else {
        options.manualSorting = undefined;
      }
    }
  } else if (configSortable !== undefined) {
    if (typeof configSortable === 'boolean') {
      sortable = configSortable;
    } else {
      sortable = true;

      if (configSortable.manualSort === true) {
        options.manualSorting = true;
      } else {
        options.manualSorting = undefined;
      }
    }
  }

  options.enableSorting = sortable;
  options.state = {
    ...options.state,
    sorting,
  };
  options.onSortingChange = setSorting;
}

const sortingFunctions = {
  text: stringlikeCompare,
  textCaseSensitive: stringlikeCompareCaseSensitive,
  number: numberlikeCompare,
  datetime: datelikeCompare,
};

/**
 * @internal
 */
export const Sorting: TableFeature = {
  createColumn<TData extends RowData, TValue>(
    column: Column<TData, TValue>,
    table: Table<TData>,
  ) {
    column.getCustomSortingFn = () => {
      if (!column) {
        throw new Error();
      }

      if (isFunction(column.columnDef.customSortingFn)) {
        return column.columnDef.customSortingFn;
      }

      if (
        typeof column.columnDef.sortType === 'string' &&
        sortingFunctions[column.columnDef.sortType]
      ) {
        return sortingFunctions[
          column.columnDef.sortType
        ] as DataTableCustomSortingFn<TValue>;
      }

      return sortingFunctions['text'] as DataTableCustomSortingFn<TValue>;
    };

    column.getAutoSortDir = () => {
      // overwrite the default from react table
      // by default all columns should be sorted ascending first
      return 'asc';
    };

    column.getCanSort = () => {
      // Grouped columns should for now not be sortable.
      if (isGroupColumnDef(column.columnDef)) {
        return false;
      }
      return (
        (column.columnDef.enableSorting ?? true) &&
        (table.options.enableSorting ?? false) &&
        !!column.accessorFn
      );
    };

    // Override toggleSorting to fix multi-sort clearing when plain-clicking
    // the last (lowest-priority) column in a multi-sort.
    // The original implementation only replaces the sorting state when the clicked column
    // is NOT the last in the sort array.
    const originalToggleSorting = column.toggleSorting;
    column.toggleSorting = (desc, isMulti) => {
      if (!isMulti) {
        const oldSorting = table.getState().sorting;

        if (oldSorting && oldSorting.length > 1) {
          const existingIndex = oldSorting.findIndex((s) => s.id === column.id);

          // In multi-sort mode, plain-clicking a sorted column should clear all other sorted columns
          // and keep only the clicked column, with its direction cycled to the next value.
          // Out-of-the-box Tanstack only does this if the clicked column is not the last in the sorting array for some reason,
          // which is why we need to override the function for specific case where the clicked column is the last in the sorting array.
          if (existingIndex === oldSorting.length - 1) {
            const nextSortingOrder = column.getNextSortingOrder();
            const nextDesc = desc ?? nextSortingOrder === 'desc';
            table.setSorting([{ id: column.id, desc: nextDesc }]);
            return;
          }
        }
      }
      originalToggleSorting(desc, isMulti);
    };
  },

  createTable<TData extends RowData>(table: Table<TData>) {
    assignRowMethods(table, {
      getSortingValue(this: Row<TData>, columnId: string) {
        if (Object.hasOwn(this._sortingValuesCache, columnId)) {
          return this._sortingValuesCache[columnId];
        }

        const column = table.getColumn(columnId);

        if (column && isColumnDefWithSortAccessor(column.columnDef)) {
          const sortAccessor = column.columnDef.sortAccessor;
          if (sortAccessor && typeof sortAccessor === 'string') {
            return sortAccessor.split('.').reduce(
              // @ts-expect-error Strict mode transition - needs to be investigated
              (obj, key) => obj[key] as Record<string, unknown>,
              this.original,
            );
          } else if (typeof sortAccessor === 'function') {
            return sortAccessor(this.original);
          }
        }

        if (!column?.accessorFn) {
          return undefined;
        }

        this._sortingValuesCache[columnId] = column.accessorFn(
          this.original,
          this.index,
        );

        return this._sortingValuesCache[columnId];
      },
    });
  },

  createRow<TData extends RowData>(row: Row<TData>, table: Table<TData>) {
    row._sortingValuesCache = {};
  },

  createHeader<TData extends RowData, TValue>(
    header: Header<TData, TValue>,
    table: Table<TData>,
  ) {
    header.getAriaSortAttribute = () => {
      if (
        table.options.enableSorting &&
        header.column.columnDef.enableSorting !== false &&
        header.column.columns.length === 0
      ) {
        const isSorted = header.column.getIsSorted();
        if (isSorted) {
          return isSorted === 'asc' ? 'ascending' : 'descending';
        }
        return 'none';
      }
      return undefined;
    };

    header.getCanSortByHeaderClick = () => {
      if (header.column.getCanColumnActions() === true) {
        return false;
      }
      return header.column.getCanSort();
    };
  },
};
